import { assets } from "./assets";
import type { ProgramSlug } from "./assets";
import type { Stats } from "@/lib/api-types";

export type HomeImpactMetricId =
  | "lives"
  | "meals"
  | "children"
  | "volunteers";

export interface HomeImpactMetric {
  id: HomeImpactMetricId;
  label: string;
  value: number;
  suffix: string;
  featured?: boolean;
}

export const homeImpact = {
  eyebrow: "Our Impact",
  title: "Lives Transformed",
  subtitle:
    "Real numbers from the communities we serve across healthcare, education, nutrition, and outreach.",
  cta: "Explore our impact",
  ctaHref: "/impact",
} as const;

export function buildHomeImpactMetrics(stats?: Stats): HomeImpactMetric[] {
  return [
    {
      id: "lives",
      label: "Lives Changed",
      value: stats?.livesImpacted ?? 50_000,
      suffix: "+",
      featured: true,
    },
    {
      id: "meals",
      label: "Meals Served",
      value: 120_000,
      suffix: "+",
    },
    {
      id: "children",
      label: "Children Educated",
      value: 8_500,
      suffix: "+",
    },
    {
      id: "volunteers",
      label: "Volunteers",
      value: 750,
      suffix: "+",
    },
  ];
}

export function splitHomeImpactMetrics(
  metrics: Array<{
    id: string;
    label: string;
    value: number;
    suffix: string;
    featured?: boolean;
  }>
) {
  const featured = metrics.find((m) => m.featured) ?? metrics[0];
  const supporting = metrics.filter((m) => m.id !== featured.id);
  return { featured, supporting };
}

export const featuredProgramSlugs = [
  "healthcare",
  "education",
  "goshala",
  "blood-donation",
] as const satisfies readonly ProgramSlug[];

export const homeHero = {
  headingLines: ["Making a Difference,", "One Life at a Time"] as const,
  /** @deprecated Use headingLines */
  title: "Making a Difference, One Life at a Time",
  subtitle:
    "AJA Abhaya Hastham is dedicated to creating positive change through comprehensive community development programs.",
};

export const homeAboutStory = {
  eyebrow: "Our Story",
  title: "A mission rooted in compassion",
  missionImage: assets.fieldWork[0],
  missionImageAlt: "Community outreach by AJA Abhaya Hastham volunteers",
  missionStatement:
    "We empower underprivileged communities through education, healthcare, and sustainable development — ensuring no one is left unseen, unheard, or unsupported.",
  highlightKeywords: [
    "empower",
    "education",
    "healthcare",
    "sustainable development",
    "unseen",
    "unsupported",
  ],
  timeline: [
    {
      year: "2020",
      title: "A vision takes root",
      description:
        "Founded as a Section 8 company with a belief that compassion must become action.",
    },
    {
      year: "2022",
      title: "Programs expand",
      description:
        "Healthcare camps, nutrition drives, and education support reach thousands of families.",
    },
    {
      year: "2024",
      title: "Community at scale",
      description:
        "Volunteers and partners unite to deliver lasting change across Telangana and beyond.",
    },
    {
      year: "Today",
      title: "Growing together",
      description:
        "Every contribution reinvested into people, communities, and a more dignified future.",
    },
  ],
} as const;

export const homeDonate = {
  title: "Offer a donation",
  subtitle: "Choose an amount and see the direct impact of your gift.",
  cta: "Offer donation",
} as const;

export const donateHero = {
  quoteMark: "\u201C",
  headline: "Be the reason",
  headlineHighlight: "someone smiles",
  headlineEnd: "today.",
  subline:
    "Your support can bring hope, happiness, and a better tomorrow.",
  thankYou: "Thank you!",
  cta: "Donate Now",
} as const;

export const donationTiers = [
  { amount: 501, impact: "Feeds 10 people in need" },
  { amount: 1101, impact: "Supports one student with supplies" },
  { amount: 5001, impact: "Funds community program outreach" },
];

export const whyAjaBullets = [
  "Registered Section 8 non-profit",
  "Eligible for 80G tax benefits",
  "Transparent fund usage",
  "Community-driven programs",
];

export const todayMetricsFallback = [
  { label: "Meals served today", value: 128 },
  { label: "Children sponsored", value: 42 },
  { label: "Families supported", value: 11 },
];

export const homeTestimonials = [
  {
    quote:
      "Seeing my contribution reach families directly gave me hope that change is possible.",
    name: "Priya S.",
    role: "Monthly donor",
    image: assets.contact[0],
  },
  {
    quote:
      "The foundation's volunteers showed up when our community needed them most.",
    name: "Ramesh K.",
    role: "Community partner",
    image: assets.contact[1],
  },
];

export const galleryPhotos = assets.fieldWork.slice(0, 5);

export function getDonateTierHref(amount: number) {
  return `/donate?amount=${amount}&category=general`;
}
